import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Chama o supervisor quando a IA precisa dele (resposta para aprovar, pergunta nova) e a
 * aba do Modo IA não está em foco: som, notificação do navegador e a contagem no título
 * da aba. Com dezenas de conversas, o supervisor está em outra aba ou outro programa; sem
 * isto a conversa ficava parada até ele voltar a olhar.
 *
 * Preferência do navegador (como a trava de aprovação): é de quem está à frente da tela.
 */

const STORAGE_KEY = 'ai-user:attention-alerts'
const SOUND_URL = '/notification.mp3'
/** Uma rajada (várias respostas prontas seguidas) vira um aviso só. */
const COOLDOWN_MS = 5_000

function readEnabled(): boolean {
    try {
        return window.localStorage.getItem(STORAGE_KEY) !== '0'
    } catch {
        return true
    }
}

function canNotify(): boolean {
    return typeof Notification !== 'undefined'
}

/** Pede a permissão de notificação. Só funciona a partir de um clique da pessoa. */
export function requestAttentionPermission(): void {
    if (canNotify() && Notification.permission === 'default') {
        void Notification.requestPermission()
    }
}

export function useSupervisorAttention(params: { keys: string[]; total: number; enabled: boolean }) {
    const { keys, total, enabled } = params
    const seenRef = useRef<Set<string> | null>(null)
    const lastAlertAtRef = useRef(0)
    const audioRef = useRef<HTMLAudioElement | null>(null)

    // Contagem no título enquanto houver algo esperando o supervisor.
    useEffect(() => {
        const base = document.title.replace(/^\(\d+\) /, '')
        document.title = total > 0 ? `(${total}) ${base}` : base
    }, [total])
    useEffect(
        () => () => {
            document.title = document.title.replace(/^\(\d+\) /, '')
        },
        [],
    )

    const keysKey = keys.join(',')
    useEffect(() => {
        const current = new Set(keys)
        // A primeira leitura (ao abrir a aba) não é novidade: não toca.
        if (seenRef.current === null) {
            seenRef.current = current
            return
        }
        const fresh = keys.filter(key => !seenRef.current!.has(key))
        seenRef.current = current
        if (!enabled || fresh.length === 0) return
        const away = document.visibilityState !== 'visible' || !document.hasFocus()
        if (!away) return
        const now = Date.now()
        if (now - lastAlertAtRef.current < COOLDOWN_MS) return
        lastAlertAtRef.current = now

        if (!audioRef.current) audioRef.current = new Audio(SOUND_URL)
        audioRef.current.currentTime = 0
        // Política de autoplay: sem clique na aba o navegador recusa; a notificação ainda vale.
        void audioRef.current.play().catch(() => undefined)

        if (canNotify() && Notification.permission === 'granted') {
            const notification = new Notification('Modo IA precisa de você', {
                body: `${total} ${total === 1 ? 'item esperando' : 'itens esperando'} na gaveta do Modo IA.`,
                tag: 'ai-user-attention',
            })
            notification.onclick = () => {
                window.focus()
                notification.close()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keysKey, enabled])
}

/** Liga/desliga o aviso; ligar pede a permissão de notificação (é um clique). */
export function useAttentionPreference() {
    const [enabled, setEnabledState] = useState(readEnabled)
    const setEnabled = useCallback((value: boolean) => {
        setEnabledState(value)
        try {
            window.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
        } catch {
            // Sem memória do navegador a escolha vale até recarregar a aba.
        }
        if (value) requestAttentionPermission()
    }, [])
    return { enabled, setEnabled }
}
